import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);

const KEY = "sps_cart_v1";

const devWarn = (...args) => {
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.warn(...args);
  }
};

function loadInitialCart() {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "[]");
  } catch (err) {
    devWarn("[cart] corrupted local cart, resetting", err?.message || err);
    return [];
  }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(loadInitialCart);

  useEffect(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify(items));
    } catch (err) {
      devWarn("[cart] failed to persist cart", err?.message || err);
    }
  }, [items]);

  const add = useCallback((product, qty = 1) => {
    setItems((cur) => {
      const idx = cur.findIndex((i) => i.product_id === product.id);
      if (idx >= 0) {
        const next = [...cur];
        next[idx] = { ...next[idx], qty: next[idx].qty + qty };
        return next;
      }
      return [
        ...cur,
        {
          product_id: product.id,
          sku: product.sku,
          name: product.name,
          price_ngn: product.price_ngn,
          image_url: product.image_url,
          qty,
        },
      ];
    });
  }, []);

  const remove = useCallback((productId) => {
    setItems((cur) => cur.filter((i) => i.product_id !== productId));
  }, []);

  const setQty = useCallback((productId, qty) => {
    setItems((cur) =>
      cur.map((i) => (i.product_id === productId ? { ...i, qty: Math.max(1, qty) } : i))
    );
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const value = useMemo(
    () => ({
      items,
      add,
      remove,
      setQty,
      clear,
      total: items.reduce((s, i) => s + i.price_ngn * i.qty, 0),
      count: items.reduce((s, i) => s + i.qty, 0),
    }),
    [items, add, remove, setQty, clear]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => useContext(CartContext);
